// @repo/observability/session — session + user-address correlation keys
//
// These primitives are consumed by the Next.js proxy helper (see
// `./nextjs.ts#mintSessionCookie`) and by server-side request handlers
// (e.g. oRPC `createContext`) to enrich structured logs with `sessionId` and
// `userAddress` fields.
//
// ⚠️  Both keys travel as cookies. The proxy only edits the raw `Cookie`
// header string: going through `RequestCookies`/`ResponseCookies` would
// re-serialize every cookie via `encodeURIComponent`, double-encoding wagmi's
// `wagmi.store` cookie and breaking `cookieToInitialState` downstream.

import type { Address } from 'viem'

import { isAddress } from 'viem'

/** Name of the HttpOnly cookie carrying the per-browser-session UUID. */
export const SESSION_ID_COOKIE = 'mo-session-id'

/**
 * Cookie set when the user has opted out of analytics. Presence of this cookie
 * (with value `'true'`) means the user has opted out; absence means default
 * opt-in, matching the existing localStorage convention in curator-v2's
 * `useAnalyticsConsent`. Read by the proxy to skip session correlation
 * entirely when the user has opted out.
 */
export const ANALYTICS_OPTED_OUT_COOKIE = 'mo-analytics-opted-out'

export interface SessionContext {
  sessionId: string | undefined
  userAddress: Address | undefined
}

/**
 * Parse a raw `Cookie` request-header value and return the decoded value of
 * `name`, or `undefined` when absent. Handles the standard `k=v; k2=v2`
 * format and URL-decodes the value.
 */
function readCookieValue(
  cookieHeader: string | null | undefined,
  name: string
): string | undefined {
  if (!cookieHeader) return undefined
  const prefix = `${name}=`
  for (const part of cookieHeader.split(';')) {
    const pair = part.trim()
    if (pair.startsWith(prefix)) {
      try {
        return decodeURIComponent(pair.slice(prefix.length))
      } catch {
        return pair.slice(prefix.length)
      }
    }
  }
  return undefined
}

/**
 * Read the session/user correlation keys from the raw `Cookie` request
 * header. Used by server-side handlers (oRPC `createContext`, server
 * components) to populate logger context.
 *
 * The `userAddress` is validated with `isAddress` because the wagmi cookie
 * is client-writable and could carry a malformed value; we never forward an
 * unvalidated string into structured logs.
 *
 * Respects analytics consent: if the `mo-analytics-opted-out` cookie is
 * `'true'`, both fields are returned as `undefined` so opted-out requests
 * never land in server-side structured logs — mirroring the gate
 * `mintSessionCookie` applies at the proxy layer. This handles the
 * window where a stale `mo-session-id` or `wagmi.store` cookie is still
 * being sent by a browser that has since opted out.
 *
 * The proxy forwards a freshly minted `mo-session-id` in the current
 * request's `Cookie` header (see `mintSessionCookie` in `./nextjs.ts`), so
 * `sessionId` is set from a user's very first request.
 */
export function readSessionContextFromCookieHeader(
  cookieHeader: string | null | undefined
): SessionContext {
  if (readCookieValue(cookieHeader, ANALYTICS_OPTED_OUT_COOKIE) === 'true') {
    return { sessionId: undefined, userAddress: undefined }
  }
  return {
    sessionId: readCookieValue(cookieHeader, SESSION_ID_COOKIE),
    userAddress: readConnectedAddressFromCookie(cookieHeader)
  }
}

/**
 * Parse wagmi's cookie-storage state to extract the currently connected EOA.
 *
 * Wagmi (configured with `createStorage({ storage: cookieStorage })`) writes a
 * `wagmi.store` cookie whose URL-encoded JSON value carries:
 *
 * ```
 * { state: { connections: { value: [[id, { accounts: [address], ... }], …] }, current: id } }
 * ```
 *
 * Returns the first account address for the currently-active connection, or
 * `undefined` when the cookie is missing, malformed, or in a disconnected
 * state. Strictly best-effort — never throws. If wagmi changes the cookie
 * schema, the fallback is "no `userAddress` in logs", which degrades
 * gracefully.
 */
export function readConnectedAddressFromCookie(
  cookieHeader: string | null | undefined
): Address | undefined {
  const raw = readCookieValue(cookieHeader, 'wagmi.store')
  if (!raw) return undefined

  try {
    const parsed = JSON.parse(raw) as {
      state?: {
        current?: string
        connections?: { value?: unknown }
      }
    }
    const current = parsed.state?.current
    const connectionsValue = parsed.state?.connections?.value
    if (!current || !Array.isArray(connectionsValue)) return undefined

    for (const entry of connectionsValue) {
      if (!Array.isArray(entry) || entry.length < 2) continue
      const [id, connection] = entry as [unknown, unknown]
      if (id !== current || typeof connection !== 'object' || connection === null) continue
      const accounts = (connection as { accounts?: unknown }).accounts
      const first = Array.isArray(accounts) ? accounts[0] : undefined
      // Narrow via `isAddress` — the cookie value is client-writable, so we
      // refuse to forward a malformed string into logs.
      if (typeof first === 'string' && isAddress(first)) return first
    }
  } catch {
    // Malformed JSON or unexpected shape — treat as no connected address.
  }
  return undefined
}
